/**
 * Activity Log Service
 * Handles API calls for activity log data
 */

import axiosConfig from '../../../../Config/axios';
import type {ActivityLogQueryParams, ActivityLogResponse} from '../Types/ActivityLogTypes';
import {getSystemCoreRoute} from "../../../../Route/RouteIndex";

const ActivityLogService = {
    /**
     * Fetch activity logs for a given subject
     * @param params Query parameters including subject_type, subject_id, and optional pagination
     * @returns Promise with activity log data
     */
    fetchActivityLogs: async (params: ActivityLogQueryParams): Promise<ActivityLogResponse> => {
        try {
            const queryParams = {
                subject_type: params.subject_type,
                subject_id: params.subject_id,
                ...(params.page && {page: params.page}),
                ...(params.limit && {limit: params.limit}),
                ...(params.sort && {sort: params.sort}),
                ...(params.log_name && {log_name: params.log_name}),
            };

            const response = await axiosConfig.get<ActivityLogResponse>(getSystemCoreRoute('activity-log'), {
                params: queryParams,
            });

            return response.data || {data: []};
        } catch (error) {
            console.error('Error fetching activity logs:', error);
            throw error;
        }
    },

    /**
     * Format properties object to readable string
     * @param properties Object containing the log properties
     * @returns Formatted string representation
     */
    formatProperties: (properties?: Record<string, any>): string => {
        if (!properties) return 'No additional details';

        const entries = Object.entries(properties)
            .map(([key, value]) => {
                // Format key from snake_case to Title Case
                const formattedKey = key
                    .split('_')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');

                // Format value
                let formattedValue = value;
                if (typeof value === 'object') {
                    formattedValue = JSON.stringify(value, null, 2);
                }

                return `${formattedKey}: ${formattedValue}`;
            })
            .join('; ');

        return entries || 'No additional details';
    },
};

export default ActivityLogService;


